import { lazy, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import { RequireAuth, RequireRole } from './components/Guards';
import { Layout, More } from './components/Layout';
import { Loading } from './components/ui';
import { SignIn } from './pages/SignIn';

// Each module lives in pages/<module>/index.tsx and exports its page components.
const Home = lazy(() => import('./pages/home').then((m) => ({ default: m.Home })));
const Bills = lazy(() => import('./pages/bills').then((m) => ({ default: m.BillsList })));
const BillPage = lazy(() => import('./pages/bills').then((m) => ({ default: m.BillPage })));
const BillNew = lazy(() => import('./pages/bills').then((m) => ({ default: m.BillNew })));
const Payments = lazy(() => import('./pages/payments').then((m) => ({ default: m.PaymentsList })));
const PaymentPage = lazy(() => import('./pages/payments').then((m) => ({ default: m.PaymentPage })));
const PaymentNew = lazy(() => import('./pages/payments').then((m) => ({ default: m.PaymentNew })));
const CashBook = lazy(() => import('./pages/payments').then((m) => ({ default: m.CashBook })));
const Acknowledgements = lazy(() => import('./pages/payments').then((m) => ({ default: m.Acknowledgements })));
const AcknowledgementPage = lazy(() => import('./pages/payments').then((m) => ({ default: m.AcknowledgementPage })));
const Expenses = lazy(() => import('./pages/expenses').then((m) => ({ default: m.ExpensesList })));
const ExpensePage = lazy(() => import('./pages/expenses').then((m) => ({ default: m.ExpensePage })));
const ExpenseNew = lazy(() => import('./pages/expenses').then((m) => ({ default: m.ExpenseNew })));
const Approvals = lazy(() => import('./pages/expenses').then((m) => ({ default: m.Approvals })));
const Reimbursements = lazy(() => import('./pages/expenses').then((m) => ({ default: m.Reimbursements })));
const RecurringPage = lazy(() => import('./pages/expenses').then((m) => ({ default: m.RecurringPage })));
const Parties = lazy(() => import('./pages/parties').then((m) => ({ default: m.PartiesList })));
const PartyPage = lazy(() => import('./pages/parties').then((m) => ({ default: m.PartyPage })));
const Sales = lazy(() => import('./pages/sales').then((m) => ({ default: m.SalesList })));
const SalesPage = lazy(() => import('./pages/sales').then((m) => ({ default: m.SalesPage })));
const SalesNew = lazy(() => import('./pages/sales').then((m) => ({ default: m.SalesNew })));
const Bank = lazy(() => import('./pages/bank').then((m) => ({ default: m.BankHome })));
const StatementPage = lazy(() => import('./pages/bank').then((m) => ({ default: m.StatementPage })));
const Journal = lazy(() => import('./pages/books').then((m) => ({ default: m.Journal })));
const EntryPage = lazy(() => import('./pages/books').then((m) => ({ default: m.EntryPage })));
const Accounts = lazy(() => import('./pages/books').then((m) => ({ default: m.Accounts })));
const Reports = lazy(() => import('./pages/books').then((m) => ({ default: m.Reports })));
const Tax = lazy(() => import('./pages/tax').then((m) => ({ default: m.Tax })));
const Partners = lazy(() => import('./pages/partners').then((m) => ({ default: m.Partners })));
const Close = lazy(() => import('./pages/partners').then((m) => ({ default: m.Close })));
const Settings = lazy(() => import('./pages/settings').then((m) => ({ default: m.Settings })));
const Documents = lazy(() => import('./pages/settings').then((m) => ({ default: m.Documents })));
const Vendor = lazy(() => import('./pages/vendor').then((m) => ({ default: m.VendorPage })));
const Assistant = lazy(() => import('./pages/assistant').then((m) => ({ default: m.AssistantPage })));
const AiProposals = lazy(() => import('./pages/assistant').then((m) => ({ default: m.ProposalsPage })));
const Playbook = lazy(() => import('./pages/assistant').then((m) => ({ default: m.PlaybookPage })));

export function App() {
  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        <Route path="/signin" element={<SignIn />} />
        <Route path="/v/:token" element={<Vendor />} />
        <Route element={<RequireAuth />}>
          <Route element={<Layout />}>
            <Route index element={<Home />} />
            <Route path="/more" element={<More />} />
            <Route path="/bills" element={<Bills />} />
            <Route path="/bills/new" element={<BillNew />} />
            <Route path="/bills/:id" element={<BillPage />} />
            <Route path="/bills/:id/edit" element={<BillNew />} />
            <Route path="/payments" element={<Payments />} />
            <Route path="/payments/new" element={<PaymentNew />} />
            <Route path="/payments/:id" element={<PaymentPage />} />
            <Route path="/cash" element={<CashBook />} />
            <Route path="/cash/acknowledgements" element={<Acknowledgements />} />
            <Route path="/cash/acknowledgements/:id" element={<AcknowledgementPage />} />
            <Route path="/expenses" element={<Expenses />} />
            <Route path="/expenses/new" element={<ExpenseNew />} />
            <Route path="/expenses/recurring" element={<RecurringPage />} />
            <Route path="/expenses/:id" element={<ExpensePage />} />
            <Route path="/expenses/:id/edit" element={<ExpenseNew />} />
            <Route path="/approvals" element={<Approvals />} />
            <Route path="/reimbursements" element={<Reimbursements />} />
            <Route path="/parties" element={<Parties />} />
            <Route path="/parties/:id" element={<PartyPage />} />
            <Route path="/sales" element={<Sales />} />
            <Route path="/sales/new" element={<SalesNew />} />
            <Route path="/sales/:id" element={<SalesPage />} />
            <Route path="/sales/:id/edit" element={<SalesNew />} />
            <Route path="/bank" element={<Bank />} />
            <Route path="/bank/:id" element={<StatementPage />} />
            <Route path="/journal" element={<Journal />} />
            <Route path="/journal/:id" element={<EntryPage />} />
            <Route path="/accounts/*" element={<Accounts />} />
            <Route path="/reports/*" element={<Reports />} />
            <Route path="/tax/*" element={<Tax />} />
            <Route path="/partners/*" element={<Partners />} />
            <Route path="/close/*" element={<Close />} />
            <Route path="/documents" element={<Documents />} />
            <Route path="/assistant/proposals" element={<AiProposals />} />
            <Route path="/assistant/playbook" element={<Playbook />} />
            {/* one route for a new conversation and /assistant/c/:id, so the page is not rebuilt when a first message gives it an id */}
            <Route path="/assistant/*" element={<Assistant />} />
            <Route element={<RequireRole roles={['admin']} />}>
              <Route path="/settings/*" element={<Settings />} />
            </Route>
            <Route path="*" element={<div className="page"><h1>Not found</h1></div>} />
          </Route>
        </Route>
      </Routes>
    </Suspense>
  );
}
